import { Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormAutocomplete from '../../../FormFields/FormAutocomplete';
import FormDEAField from '../../../FormFields/FormDEANumber';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormNPIField from '../../../FormFields/FormNPINumber';
import FormPhoneNumberField from '../../../FormFields/FormPhoneNumField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { CLINICAL } from '../../../formQuestionsEnum';
import { formCardStyle } from '../../../TransitionStyles';
import { validateAddress, validateEmail, validateZip } from '../../../ValidateFunctions';

MedicalDirectorForm.propTypes = {
  permissions: PropTypes.object,
};

export default function MedicalDirectorForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const fieldIds = [
    CLINICAL.MD_FIRST_NAME.id,
    CLINICAL.MD_LAST_NAME.id,
    CLINICAL.MD_ADDRESS.id,
    CLINICAL.MD_CITY.id,
    CLINICAL.MD_STATE.id,
    CLINICAL.MD_ZIP.id,
    CLINICAL.MD_PHONE_NUMBER.id,
    CLINICAL.MD_FAX.id,
    CLINICAL.MD_EMAIL.id,
    CLINICAL.MD_COMPANY.id,
    CLINICAL.NPI_NUMBER.id,
    CLINICAL.DEA_NUMBER.id,
    CLINICAL.MEDICAL_DIRECTOR_LICENSE.id,
    CLINICAL.MEDICAL_DIRECTOR_CONTRACT.id,
  ];

  const fiftyStates = useSelector((state) => state.transitions?.data?.fiftyStates);

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Medical Director
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {CLINICAL.MD_FIRST_NAME.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'First name' }} />
                <FormTextField
                  fieldName={CLINICAL.MD_FIRST_NAME.fieldName}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.MD_FIRST_NAME.id],
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {CLINICAL.MD_LAST_NAME.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Last name' }} />
              <FormTextField
                fieldName={CLINICAL.MD_LAST_NAME.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_LAST_NAME.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_COMPANY.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Company' }} />
              <FormTextField
                fieldName={CLINICAL.MD_COMPANY.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_COMPANY.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_ADDRESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Address' }} />
              <FormTextField
                fieldName={CLINICAL.MD_ADDRESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_ADDRESS.id],
                  validation: {
                    validate: validateAddress,
                  },
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_CITY.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'City' }} />
              <FormTextField
                fieldName={CLINICAL.MD_CITY.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_CITY.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_STATE.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'State' }} />
              <FormAutocomplete
                fieldName={CLINICAL.MD_STATE.fieldName}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_STATE.id],
                }}
                options={fiftyStates || []}
              />
            </Grid>
          )}
          {CLINICAL.MD_ZIP.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Zip' }} />
              <FormTextField
                fieldName={CLINICAL.MD_ZIP.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_ZIP.id],
                  validation: {
                    pattern: { ...validateZip },
                  },
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_PHONE_NUMBER.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Phone' }} />
              <FormPhoneNumberField
                fieldName={CLINICAL.MD_PHONE_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_PHONE_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_FAX.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Fax' }} />
              <FormPhoneNumberField
                fieldName={CLINICAL.MD_FAX.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_FAX.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MD_EMAIL.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Email' }} />
              <FormTextField
                fieldName={CLINICAL.MD_EMAIL.fieldName}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.MD_EMAIL.id],
                  validation: {
                    pattern: { ...validateEmail },
                  },
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {CLINICAL.NPI_NUMBER.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'NPI#' }} />
              <FormNPIField
                fieldName={CLINICAL.NPI_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.NPI_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.DEA_NUMBER.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'DEA#' }} />
              <FormDEAField
                fieldName={CLINICAL.DEA_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[CLINICAL.DEA_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {CLINICAL.MEDICAL_DIRECTOR_LICENSE.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FieldContainer>
                <FormTitle
                  fieldAttributes={{ questionTitle: 'License for facility medical director', fieldType: 'file' }}
                />
                <FormFileDrop
                  fieldName={CLINICAL.MEDICAL_DIRECTOR_LICENSE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.MEDICAL_DIRECTOR_LICENSE.id],
                    fileTypeId: FILE_TYPES.MEDICAL_DIRECTOR_LICENSE,
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {CLINICAL.MEDICAL_DIRECTOR_CONTRACT.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Upload contract', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={CLINICAL.MEDICAL_DIRECTOR_CONTRACT.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[CLINICAL.MEDICAL_DIRECTOR_CONTRACT.id],
                    fileTypeId: FILE_TYPES.MEDICAL_DIRECTOR_CONTRACT,
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
